import { Auth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import { supabase } from '../../lib/supabase';
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Image,
  useBreakpointValue,
  VStack,
  useColorModeValue,
  Link,
  Button,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth/AuthProvider';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../../components/v0/LanguageSwitcher';

export const Login = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [testLoginLoading, setTestLoginLoading] = useState(false);
  const [dimLoginLoading, setDimLoginLoading] = useState(false);
  const { t, i18n } = useTranslation(['v0/auth', 'v0/common', 'common']);

  const maxW = useBreakpointValue({ base: '90%', md: '900px' });
  const authWidth = useBreakpointValue({ base: '100%', md: '400px' });
  const showIllustration = useBreakpointValue({ base: false, md: true });

  // 颜色 - Airbnb 风格
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.100');
  const subtextColor = useColorModeValue('gray.500', 'gray.400');

  // Airbnb 品牌色
  const airbnbRed = '#FF5A5F';
  const airbnbDarkRed = '#FF385C';

  // 输入框背景色、文字色
  const inputBg = useColorModeValue('white', 'gray.700');
  const inputBorderColor = useColorModeValue('gray.800', 'gray.200');
  const buttonBg = useColorModeValue(airbnbRed, airbnbDarkRed);
  const socialButtonBg = useColorModeValue('white', 'gray.200');
  const socialButtonBorder = useColorModeValue('gray.800', 'gray.200');
  const socialButtonText = useColorModeValue('gray.700', 'gray.800');

  // 自定义Supabase Auth UI主题 - Airbnb 风格
  const customTheme = {
    default: {
      colors: {
        brand: airbnbRed,
        brandAccent: airbnbDarkRed,
        brandButtonText: 'white',
        inputBackground: inputBg,
        inputBorder: useColorModeValue('#ccc', '#666'),
        inputText: textColor,
        inputPlaceholder: subtextColor,
        buttonBackground: buttonBg,
        buttonText: 'white',
        buttonBorder: 'transparent',
        // 社交登录按钮
        modalBorder: borderColor,
        dividerBackground: borderColor,
        // Supabase Auth UI标准变量名
        defaultButtonBackground: socialButtonBg,
        defaultButtonBackgroundHover: useColorModeValue('gray.100', 'gray.300'),
        defaultButtonBorder: useColorModeValue('#ccc', '#666'),
        defaultButtonText: socialButtonText,
      },
      space: {
        buttonPadding: '12px 15px',
        inputPadding: '12px 15px',
      },
      borderWidths: {
        buttonBorderWidth: '1px',
        inputBorderWidth: '1px',
      },
      radii: {
        borderRadiusButton: '8px',
        buttonBorderRadius: '8px',
        inputBorderRadius: '8px',
      },
      fontSizes: {
        baseBodySize: '14px',
        baseInputSize: '16px',
        baseButtonSize: '16px',
      },
      fonts: {
        bodyFontFamily: `"Circular", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif`,
        buttonFontFamily: `"Circular", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif`,
        inputFontFamily: `"Circular", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif`,
      },
    },
  };

  // 测试用户登录 (testui@tempo.app)
  const handleTestLogin = async () => {
    setTestLoginLoading(true);
    try {
      console.log('[Login] Using test account login...');

      const { data, error } = await supabase.auth.signInWithPassword({
        email: 'testui@tempo.app',
        password: 'Test@123456',
      });

      if (error) {
        console.error('[Login] Test login error:', error.message);
        alert(t('v0/auth:login.testLoginFailed', { message: error.message }));
      } else {
        console.log('[Login] Test login successful:', data.user?.id);
      }
    } catch (err) {
      console.error('[Login] Unexpected test login error:', err);
      alert(t('v0/auth:login.testLoginError'));
    } finally {
      setTestLoginLoading(false);
    }
  };

  // Dim 快速登录 (dimpurr@live.com)
  const handleDimLogin = async () => {
    setDimLoginLoading(true);
    try {
      console.log('[Login] Using Dim account login...');

      const { data, error } = await supabase.auth.signInWithPassword({
        email: 'dimpurr@live.com',
        password: 'SharePass1',
      });

      if (error) {
        console.error('[Login] Dim login error:', error.message);
        alert(t('v0/auth:login.testLoginFailed', { message: error.message }));
      } else {
        console.log('[Login] Dim login successful:', data.user?.id);
      }
    } catch (err) {
      console.error('[Login] Unexpected Dim login error:', err);
      alert(t('v0/auth:login.testLoginError'));
    } finally {
      setDimLoginLoading(false);
    }
  };

  useEffect(() => {
    // Only redirect if not loading and user exists
    if (!loading && user) {
      console.log('[LoginV0] User detected, redirecting to /v0/tasks...', user);
      navigate('/v0/tasks', { replace: true }); // Redirect to v0 tasks page
    } else {
      // Add more detail to the else case log
      console.log('[LoginV0] useEffect check. State:', { loading, userExists: !!user });
    }
  }, [user, loading, navigate]);

  return (
    <Box bg={bgColor} minH="100vh" pt={{ base: 8, md: 0 }}>
      <Container maxW={maxW} py={{ base: 6, md: 16 }} height="100%">
        <Box position="absolute" top={4} right={4}>
          <LanguageSwitcher placement="bottom" />
        </Box>
        <Flex
          direction={{ base: 'column', md: 'row' }}
          bg={cardBg}
          borderRadius="xl"
          overflow="hidden"
          boxShadow="lg"
          borderWidth="1px"
          borderColor={borderColor}
        >
          {/* 左侧图片/插图区域 - 仅在平板和桌面显示 */}
          {showIllustration && (
            <Flex
              flex="1.2"
              bg={airbnbRed}
              color="white"
              align="center"
              justify="center"
              p={8}
              direction="column"
            >
              {/* TODO: Optimize this image: download locally, convert to WebP, resize to e.g., 400px width for 2x DPI. */}
              <Image
                // src="https://cdn-icons-png.flaticon.com/512/1055/1055683.png"
                // src="/assets/images/login-illustration.webp" // 假设新路径和格式
                src="/login-logo.png" // 使用复制过来的 logo
                alt="Productivity"
                maxW="200px"
                width="200px" // 添加明确宽度 (根据 maxW)
                height="200px" // 添加明确高度 (假设是正方形，请根据实际图片调整)
                fetchPriority="high" // 提高 LCP 图片加载优先级 (驼峰命名)
                mb={6}
              />
              <Heading size="lg" mb={4} textAlign="center">{t('v0/auth:login.title')}</Heading>
              <Text fontSize="lg" textAlign="center">
                {t('v0/auth:login.subtitle')}
              </Text>
              <VStack mt={8} spacing={3} align="center">
                <Text fontSize="sm" fontWeight="medium">{t('v0/auth:features.title')}</Text>
                <Flex wrap="wrap" justify="center" gap={2}>
                  {[
                    t('v0/auth:features.taskManagement'),
                    t('v0/auth:features.projectTracking'),
                    t('v0/auth:features.aiAssistant'),
                    t('v0/auth:features.collaboration')
                  ].map((feature) => (
                    <Box
                      key={feature}
                      bg="rgba(255,255,255,0.2)"
                      px={3}
                      py={1}
                      borderRadius="full"
                      fontSize="xs"
                    >
                      {feature}
                    </Box>
                  ))}
                </Flex>
              </VStack>
            </Flex>
          )}

          {/* 右侧登录表单区域 */}
          <Box
            flex="1"
            p={{ base: 6, md: 8 }}
            width={{ base: '100%', md: authWidth }}
          >
            {/* 移动端Logo和标题 */}
            {!showIllustration && (
              <VStack mb={8} spacing={2}>
                {/* TODO: Optimize this image: download locally, convert to WebP. */}
                <Image
                  // src="https://cdn-icons-png.flaticon.com/512/1055/1055683.png"
                  // src="/assets/images/login-logo.webp" // 假设新路径和格式
                  src="/login-logo.png" // 使用复制过来的 logo
                  alt="Tempo Logo"
                  boxSize="80px"
                />
                <Heading size="md" color={textColor}>{t('v0/auth:login.welcomeTitle')}</Heading>
                <Text color={subtextColor} textAlign="center">
                  {t('v0/auth:login.welcomeSubtitle')}
                </Text>
              </VStack>
            )}

            {/* 快速登录按钮 */}
            <Flex direction="column" gap={2} mb={4}>
              {/* 测试用户登录按钮 */}
              <Button
                w="100%"
                colorScheme="cyan"
                onClick={handleTestLogin}
                isLoading={testLoginLoading}
                loadingText={t('v0/auth:login.loadingText')}
              >
                {t('v0/auth:login.testUserLogin')}
              </Button>

              {/* Dim 快速登录按钮 */}
              <Button
                w="100%"
                colorScheme="purple"
                onClick={handleDimLogin}
                isLoading={dimLoginLoading}
                loadingText={t('v0/auth:login.loadingText')}
              >
                {t('v0/auth:login.dimQuickLogin')}
              </Button>
            </Flex>

            <Auth
              supabaseClient={supabase}
              appearance={{
                theme: ThemeSupa,
                variables: customTheme,
                style: {
                  button: {
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    transform: 'scale(1)',
                    color: useColorModeValue('gray.800', 'white'),
                  },
                  anchor: {
                    color: airbnbRed,
                    fontWeight: '500',
                    textDecoration: 'none',
                  },
                  container: {
                    borderWidth: '0px',
                  },
                  divider: {
                    margin: '1.5em 0',
                  },
                  label: {
                    marginBottom: '4px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: textColor,
                  },
                  input: {
                    transition: 'border-color 0.2s ease',
                    color: textColor,
                    background: inputBg,
                  },
                  message: {
                    color: textColor,
                  },
                }
              }}
              providers={['github', 'google', 'apple']}
              redirectTo={`${window.location.origin}/auth/callback`}
              view="sign_in"
              showLinks={true}
              localization={{
                variables: {
                  sign_in: {
                    email_label: t('v0/auth:supabase.email_label'),
                    password_label: t('v0/auth:supabase.password_label'),
                    button_label: t('v0/auth:supabase.sign_in_button'),
                    loading_button_label: t('v0/auth:supabase.sign_in_loadingText'),
                    link_text: t('v0/auth:supabase.sign_in_link_text'),
                    social_provider_text: t('v0/auth:supabase.social_provider_text'),
                  },
                  sign_up: {
                    email_label: t('v0/auth:supabase.email_label'),
                    password_label: t('v0/auth:supabase.password_label'),
                    button_label: t('v0/auth:supabase.sign_up_button'),
                    loading_button_label: t('v0/auth:supabase.sign_up_loadingText'),
                    link_text: t('v0/auth:supabase.sign_up_link_text'),
                    social_provider_text: t('v0/auth:supabase.social_provider_text'),
                  },
                  forgotten_password: {
                    email_label: t('v0/auth:supabase.email_label'),
                    password_label: t('v0/auth:supabase.password_label'),
                    button_label: t('v0/auth:supabase.forgot_password_button'),
                    loading_button_label: t('v0/auth:supabase.forgot_password_loadingText'),
                    link_text: t('v0/auth:supabase.forgot_password_link_text'),
                  }
                }
              }}
            />

            <Text fontSize="xs" color={subtextColor} textAlign="center" mt={8}>
              {t('v0/auth:legal.agreement')}
              <Link color={airbnbRed} href="#" mx={1}>{t('v0/auth:legal.terms')}</Link>
              {t('v0/auth:legal.and')}
              <Link color={airbnbRed} href="#" mx={1}>{t('v0/auth:legal.privacy')}</Link>
            </Text>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};